import React, { useEffect } from "react";
import { CheckCircle, X, Clock, ShieldCheck, ArrowRight } from "lucide-react";

function SuccessModal({ isOpen, onClose, data = {} }) {
  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-black/65 backdrop-blur-sm transition-opacity duration-300 overflow-x-hidden overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[430px] max-h-[90vh] overflow-y-auto overflow-x-hidden rounded-[22px] sm:rounded-[28px] border border-[#d8e8de] bg-white p-5 sm:p-7 text-center shadow-[0_25px_70px_rgba(0,0,0,0.25)] animate-modalPop"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Contained Soft Background Mint Aura (No Horizontal Overflow) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-[22px] sm:rounded-[28px]"
        >
          <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[radial-gradient(circle,rgba(46,229,162,0.25),transparent_70%)] blur-xl" />
        </div>

        {/* Close 'X' Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close modal"
          className="absolute right-3.5 top-3.5 sm:right-5 sm:top-5 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-[#f3f4f6] text-[#6b7280] transition-all hover:bg-[var(--hero-green)] hover:text-white cursor-pointer"
        >
          <X size={16} strokeWidth={2.4} />
        </button>

        {/* Animated Checkmark Badge */}
        <div className="mx-auto mt-2 flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-[#e8f5e9] text-[var(--hero-green)] shadow-md animate-bounce-subtle relative z-10">
          <CheckCircle size={36} strokeWidth={2.2} className="sm:hidden" />
          <CheckCircle size={40} strokeWidth={2.2} className="hidden sm:block" />
        </div>

        {/* Title */}
        <h3 className="mt-4 sm:mt-5 font-bricolage text-[22px] sm:text-[25px] font-bold tracking-tight text-[#111827] relative z-10">
          Thank You for Applying!
        </h3>

        {/* Professional Body Message */}
        <p className="mt-2 sm:mt-2.5 font-jakarta text-[13px] sm:text-[14.5px] leading-[1.6] text-[#4b5563] relative z-10">
          Your application for{" "}
          <span className="font-bold text-[var(--hero-green)]">
            {data?.loanType || "Loan"}
          </span>{" "}
          has been successfully submitted. Our dedicated loan specialist will contact you shortly.
        </p>

        {/* Quick Turnaround Highlights */}
        <div className="mt-4 sm:mt-5 rounded-2xl bg-[#f0faf4] border border-[#d4ecdf] p-3.5 sm:p-4 text-left space-y-2 relative z-10">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <Clock className="h-4 w-4 text-[var(--hero-green)] shrink-0" />
            <span className="font-jakarta text-[12px] sm:text-[13px] text-[#2c3e35]">
              Expected callback: <strong className="text-[#111827]">Within 15 minutes</strong>
            </span>
          </div>
          <div className="flex items-center gap-2.5 sm:gap-3">
            <ShieldCheck className="h-4 w-4 text-[var(--hero-green)] shrink-0" />
            <span className="font-jakarta text-[12px] sm:text-[13px] text-[#2c3e35]">
              Status: <strong className="text-[#008751]">Verified & Priority Routed</strong>
            </span>
          </div>
        </div>

        {/* Done Action Button */}
        <div className="mt-5 sm:mt-6 relative z-10">
          <button
            type="button"
            onClick={onClose}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--hero-green)] py-3 sm:py-3.5 font-jakarta text-[14px] sm:text-[15px] font-bold text-white shadow-md transition-all hover:bg-[var(--hero-green-hover)] hover:-translate-y-0.5 cursor-pointer"
          >
            <span>Done</span>
            <ArrowRight size={16} strokeWidth={2.4} />
          </button>
        </div>
      </div>

      {/* Bounce Animation Keyframes */}
      <style>{`
        @keyframes bounceSubtle {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.06);
          }
        }
        .animate-bounce-subtle {
          animation: bounceSubtle 2.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default SuccessModal;
